import { QueryClient, QueryClientProvider } from "@tanstack/preact-query"
import { Fragment, useEffect, useState } from "react"
import { Link, Redirect, Route, Switch, useLocation } from "wouter"

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar"

import { getKey, setKey, UNAUTHORIZED } from "./api"
import { LogsPage } from "./pages/logs"
import { SessionsPage } from "./pages/sessions"
import { SessionDetailPage } from "./pages/sessions/detail"
import { StatsPage } from "./pages/stats"
import { AppSidebar, crumbs } from "./sidebar"

export function App() {
  const [location] = useLocation()
  const [key, setKeyState] = useState(getKey)
  const [error, setError] = useState("")
  const list = crumbs(location)

  useEffect(() => {
    const on = () => {
      setKeyState("")
      setError("认证失败：Gateway API Key 无效或已变更，请重新输入。")
    }
    addEventListener(UNAUTHORIZED, on)
    return () => removeEventListener(UNAUTHORIZED, on)
  }, [])

  const [client] = useState(() => new QueryClient())

  if (!key) {
    return <KeyGate error={error} onSave={(k) => (setKey(k), setKeyState(k))} />
  }

  return (
    <QueryClientProvider client={client}>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <header className="flex h-12 shrink-0 items-center gap-2 px-4">
            <SidebarTrigger />
            <Breadcrumb>
              <BreadcrumbList>
                {list.map((crumb, i) => (
                  <Fragment key={crumb.title}>
                    <BreadcrumbItem>
                      {i < list.length - 1 ? (
                        <BreadcrumbLink render={<Link href={crumb.href!} />}>
                          {crumb.title}
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage
                          className={list.length > 1 ? "max-w-40 truncate font-mono" : undefined}
                        >
                          {crumb.title}
                        </BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {i < list.length - 1 && <BreadcrumbSeparator />}
                  </Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </header>
          <div className="flex flex-1 flex-col gap-4">
            <div className="mx-auto w-full max-w-5xl">
              <Switch>
                <Route path="/stats" component={StatsPage} />
                <Route path="/logs" component={LogsPage} />
                <Route path="/sessions" component={SessionsPage} />
                <Route path="/sessions/:id" component={SessionDetailPage} />
                <Route>
                  <Redirect to="/stats" />
                </Route>
              </Switch>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </QueryClientProvider>
  )
}

function KeyGate(props: { error?: string; onSave: (key: string) => void }) {
  const [value, setValue] = useState("")
  return (
    <div className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>API Key</CardTitle>
          <CardDescription className={props.error ? "text-destructive" : undefined}>
            {props.error || "Enter the gateway API key to continue."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              if (value) {
                props.onSave(value)
              }
            }}
          >
            <Input
              type="password"
              value={value}
              placeholder="sk-..."
              onChange={(e) => setValue(e.currentTarget.value)}
            />
            <Button type="submit">Save</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
